interface Place {
  id: number;
  lat: number;
  lng: number;
  [key: string]: any;
}

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

// Calculate if a point is within bounds with buffer
export function isPlaceInBounds(
  place: Place,
  bounds: MapBounds,
  bufferPercent: number = 0.2
): boolean {
  const latBuffer = (bounds.north - bounds.south) * bufferPercent;
  const lngBuffer = (bounds.east - bounds.west) * bufferPercent;

  const expandedBounds = {
    north: bounds.north + latBuffer,
    south: bounds.south - latBuffer,
    east: bounds.east + lngBuffer,
    west: bounds.west - lngBuffer
  };

  return (
    place.lat >= expandedBounds.south &&
    place.lat <= expandedBounds.north &&
    place.lng >= expandedBounds.west &&
    place.lng <= expandedBounds.east
  );
}

// Get visible places within map bounds
export function getVisiblePlaces(
  places: Place[],
  bounds: MapBounds,
  bufferPercent: number = 0.2
): Place[] {
  return places.filter(place => isPlaceInBounds(place, bounds, bufferPercent));
}

// Debounce function for search and map updates
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

// Throttle function for scroll and resize events
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

// Calculate map bounds from center and zoom
export function calculateBoundsFromCenter(
  center: { lat: number; lng: number },
  zoom: number
): MapBounds {
  // Approximate calculation - adjust based on zoom level
  const latDelta = 180 / Math.pow(2, zoom);
  const lngDelta = 360 / Math.pow(2, zoom);

  return {
    north: center.lat + latDelta / 2,
    south: center.lat - latDelta / 2,
    east: center.lng + lngDelta / 2,
    west: center.lng - lngDelta / 2
  };
}